import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FiArrowLeft, FiCheckSquare, FiChevronDown, FiGrid, FiLogOut, FiMenu, FiMoon, FiSearch,
    FiSettings, FiSun, FiBell, FiCheck,
} from "react-icons/fi";

import apiService from "../api/apiService";
import useAuth from "../hooks/useAuth";
import { useTheme } from "../context/ThemeContext";
import { useUI } from "../context/UIContext";
import { localizedPath } from "../utils/routeHelpers";
import { ADMIN_PERMISSION_KEYS } from "../constants/adminModules";
import { USER_TYPE_LABELS } from "../constants/userTypes";
import AccessibilityMenu from "./AccessibilityMenu";
import styles from "./Header.module.css";

/**
 * Pamięć przycisku „Wróć" dla panelu administratora (wzorzec z nagłówka mPracownika —
 * przycisk siedzi w TYM SAMYM miejscu: lewy górny róg, zaraz za hamburgerem).
 *
 *  • bp_admin_return_to  — skąd użytkownik WSZEDŁ do panelu; z /admin wraca dokładnie tam,
 *  • bp_admin_last_module — ostatnio otwarty moduł; panel podświetla go jako „ostatni stan".
 *
 * sessionStorage, nie localStorage: pamięć nawigacji jest sensowna w obrębie karty,
 * a po tygodniu nie ma wracać do przypadkowej strony sprzed urlopu.
 */
const ADMIN_RETURN_KEY = "bp_admin_return_to";
const ADMIN_LAST_MODULE_KEY = "bp_admin_last_module";

/**
 * Identyfikatory paneli rozwijanych. Przycisk musi wskazywać swój panel przez
 * aria-controls, a nagłówek renderuje się w aplikacji dokładnie raz — stała jest więc
 * pewniejsza od identyfikatora losowanego przy każdym renderze.
 */
const BELL_PANEL_ID = "header-notifications-panel";
const USER_MENU_ID = "header-user-menu";

/**
 * Polska odmiana po liczebniku — bez niej czytnik ekranu czyta „1 nieprzeczytanych".
 * Etykieta dzwonka jest jedynym miejscem, w którym liczba nieprzeczytanych trafia do
 * czytnika (na ekranie niesie ją sama czerwona plakietka).
 */
const opisNieprzeczytanych = (ile) => {
    if (ile === 1) return "1 nieprzeczytane powiadomienie";
    const jednosci = ile % 10;
    const dziesiatki = ile % 100;
    if (jednosci >= 2 && jednosci <= 4 && (dziesiatki < 12 || dziesiatki > 14)) {
        return `${ile} nieprzeczytane powiadomienia`;
    }
    return `${ile} nieprzeczytanych powiadomień`;
};

/**
 * Fokus wchodzi w świeżo otwarte menu — role="menu" zapowiada czytnikowi ekranu
 * nawigację strzałkami, więc kursor musi już być w środku. preventScroll, bo na
 * telefonie samo ustawienie fokusu potrafi przewinąć stronę pod nagłówkiem.
 */
const focusFirstMenuItem = (container) => {
    if (!container) return;
    const first = container.querySelector('[role="menuitem"]');
    (first || container).focus({ preventScroll: true });
};

/** Strzałki/Home/End krążą po pozycjach menu — obsługa wspólna dla dzwonka i menu konta. */
const handleMenuKeyDown = (event) => {
    const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!keys.includes(event.key)) return;
    const items = Array.from(event.currentTarget.querySelectorAll('[role="menuitem"]'));
    if (items.length === 0) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement);
    let next;
    if (event.key === "Home") next = 0;
    else if (event.key === "End") next = items.length - 1;
    else if (event.key === "ArrowDown") next = current < 0 ? 0 : (current + 1) % items.length;
    else next = current <= 0 ? items.length - 1 : current - 1;
    items[next].focus();
};

const Header = () => {
    const { user, logout, hasPermission } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { toggleMobileSidebar } = useUI();
    const navigate = useNavigate();
    const location = useLocation();

    // ── Przycisk „Wróć" panelu administratora ────────────────────────────────────
    const isAdminArea = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
    const onAdminHome = location.pathname === "/admin";
    const previousPathRef = useRef(null);

    useEffect(() => {
        const previous = previousPathRef.current;
        const current = location.pathname;
        try {
            // Wejście do strefy /admin* spoza niej → zapamiętaj punkt powrotu.
            if ((current === "/admin" || current.startsWith("/admin/"))
                && previous && previous !== "/admin" && !previous.startsWith("/admin/")) {
                sessionStorage.setItem(ADMIN_RETURN_KEY, previous);
            }
            // Każdy moduł panelu zapisuje się jako „ostatni stan".
            if (current.startsWith("/admin/")) {
                sessionStorage.setItem(ADMIN_LAST_MODULE_KEY, current);
            }
        } catch {
            /* tryb prywatny — przycisk działa dalej, tylko bez pamięci punktu powrotu */
        }
        previousPathRef.current = current;
    }, [location.pathname]);

    const handleAdminBack = () => {
        if (!onAdminHome) {
            // Z modułu wracamy do spisu kafelków — panel sam podświetli ostatnio otwarty.
            navigate(localizedPath("adminPanel"));
            return;
        }
        let target = null;
        try {
            target = sessionStorage.getItem(ADMIN_RETURN_KEY);
        } catch {
            /* jak wyżej */
        }
        navigate(target && target.startsWith("/") && !target.startsWith("//")
            ? target
            : localizedPath("dashboard"));
    };

    const [menuOpen, setMenuOpen] = useState(false);
    const [query, setQuery] = useState("");

    // Powiadomienia w aplikacji (dzwonek). Odświeżane co 60 s — to komunikaty typu
    // „wniosek czeka na akceptację", więc opóźnienie minuty jest bez znaczenia,
    // a częstsze odpytywanie obciążałoby serwer bez korzyści.
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [bellOpen, setBellOpen] = useState(false);
    const bellRef = useRef(null);
    const bellBtnRef = useRef(null);
    const bellPanelRef = useRef(null);

    const loadNotifications = useCallback(async () => {
        try {
            const res = await apiService.getNotifications();
            setNotifications(res.data?.items || []);
            setUnreadCount(res.data?.unread || 0);
        } catch {
            // brak powiadomień nie może psuć nagłówka
        }
    }, []);

    useEffect(() => {
        loadNotifications();
        const timer = setInterval(loadNotifications, 60000);
        return () => clearInterval(timer);
    }, [loadNotifications]);

    // Zamknięcie listy kliknięciem poza nią albo klawiszem Escape. Po Escape fokus wraca
    // na dzwonek — inaczej osoba na klawiaturze ląduje na początku dokumentu i musi
    // przetabować cały nagłówek od nowa.
    useEffect(() => {
        if (!bellOpen) return undefined;
        const onClick = (event) => {
            if (bellRef.current && !bellRef.current.contains(event.target)) {
                setBellOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setBellOpen(false);
                bellBtnRef.current?.focus();
            }
        };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [bellOpen]);

    useEffect(() => {
        if (bellOpen) focusFirstMenuItem(bellPanelRef.current);
    }, [bellOpen]);

    const openNotification = async (item) => {
        setBellOpen(false);
        if (!item.isRead) {
            try {
                await apiService.markNotificationRead(item.id);
                setUnreadCount((value) => Math.max(0, value - 1));
                setNotifications((prev) =>
                    prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n))
                );
            } catch {
                /* oznaczenie nie może blokować przejścia do wniosku */
            }
        }
        if (item.link) {
            navigate(item.link);
            return;
        }
        // Powiadomienie bez odnośnika tylko znika z listy — fokus musi mieć dokąd wrócić,
        // bo klikniętego przycisku już nie ma w drzewie.
        bellBtnRef.current?.focus();
    };

    const markAllRead = async () => {
        try {
            await apiService.markAllNotificationsRead();
            setUnreadCount(0);
            setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        } catch {
            /* nic — lista odświeży się przy następnym cyklu */
        }
    };
    const menuRef = useRef(null);
    const userBtnRef = useRef(null);
    const userMenuRef = useRef(null);
    const searchRef = useRef(null);

    const initials = `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() || "?";
    const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

    // Skróty w nagłówku prowadzą tylko tam, gdzie użytkownik faktycznie wejdzie — inaczej klik
    // kończy się ekranem „Brak dostępu".
    const mozeSkanowac = hasPermission("GuardScan");
    // Pozycja „Panel administratora" prowadzi do SPISU kafelków (/admin) — panel sam pokaże,
    // które moduły są dla tej osoby dostępne. Widoczna dla każdego z choć jednym kluczem
    // administracyjnym (ta sama lista, z której zbudowane jest menu i kafelki).
    const adminTarget = useMemo(
        () => (hasPermission(ADMIN_PERMISSION_KEYS) ? "adminPanel" : null),
        [hasPermission]
    );

    // Menu konta zamyka się tak samo jak dzwonek: klik poza nim albo Escape z powrotem
    // fokusu na przycisk profilu.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setMenuOpen(false);
                userBtnRef.current?.focus();
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onClickOutside);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [menuOpen]);

    useEffect(() => {
        if (menuOpen) focusFirstMenuItem(userMenuRef.current);
    }, [menuOpen]);

    // Ctrl/⌘ + K stawia kursor w wyszukiwarce — skrót znany z mPracownika.
    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const submitSearch = useCallback(
        (event) => {
            event.preventDefault();
            const phrase = query.trim();
            // Wyszukiwarka prowadzi na listę przepustek z gotową frazą — filtruje już sama strona.
            navigate(
                phrase
                    ? `${localizedPath("passes")}?szukaj=${encodeURIComponent(phrase)}`
                    : localizedPath("passes")
            );
        },
        [navigate, query]
    );

    // logout() z AuthContext sam odsyła na ekran logowania.
    const handleLogout = () => {
        setMenuOpen(false);
        logout();
    };

    return (
        <header className={styles.header}>
            <div className={styles.leftSection}>
                <button
                    type="button"
                    className={styles.hamburgerBtn}
                    onClick={toggleMobileSidebar}
                    title="Menu"
                    aria-label="Menu"
                >
                    <FiMenu />
                </button>

                {/* „Wróć" panelu administratora — to samo miejsce co w mPracowniku (lewy górny
                    róg, za hamburgerem). Z modułu wraca do spisu kafelków, ze spisu — tam,
                    skąd się do panelu weszło. Na telefonie zostaje sama strzałka. */}
                {isAdminArea && (
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={handleAdminBack}
                        title={onAdminHome ? "Powrót" : "Panel administratora"}
                    >
                        <FiArrowLeft size={15} />
                        <span>{onAdminHome ? "Powrót" : "Panel administratora"}</span>
                    </button>
                )}

                <form className={styles.searchWrap} onSubmit={submitSearch} role="search">
                    <span className={styles.searchIcon}><FiSearch size={15} /></span>
                    <input
                        ref={searchRef}
                        type="search"
                        className={styles.searchBar}
                        placeholder="Szukaj przepustek..."
                        aria-label="Szukaj przepustek"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <kbd className={styles.searchKbd}>Ctrl K</kbd>
                </form>
            </div>

            <div className={styles.headerActions}>
                <div className={styles.actionsGroup}>
                    {/* Dzwonek powiadomień — m.in. „wniosek czeka na akceptację". */}
                    <div className={styles.bellWrap} ref={bellRef}>
                        <button
                            type="button"
                            ref={bellBtnRef}
                            className={styles.actionBtn}
                            onClick={() => setBellOpen((value) => !value)}
                            title="Powiadomienia"
                            aria-label={
                                unreadCount > 0
                                    ? `Powiadomienia, ${opisNieprzeczytanych(unreadCount)}`
                                    : "Powiadomienia, brak nieprzeczytanych"
                            }
                            aria-haspopup="menu"
                            aria-expanded={bellOpen}
                            aria-controls={bellOpen ? BELL_PANEL_ID : undefined}
                        >
                            <FiBell size={16} aria-hidden="true" />
                            {/* Plakietka jest już policzona w aria-label przycisku — czytnik nie
                                ma jej odczytywać drugi raz jako gołą liczbę. */}
                            {unreadCount > 0 && (
                                <span className={styles.bellBadge} aria-hidden="true">
                                    {unreadCount > 9 ? "9+" : unreadCount}
                                </span>
                            )}
                        </button>

                        {bellOpen && (
                            <div
                                id={BELL_PANEL_ID}
                                ref={bellPanelRef}
                                className={styles.bellPanel}
                                role="menu"
                                aria-label="Powiadomienia"
                                tabIndex={-1}
                                onKeyDown={handleMenuKeyDown}
                            >
                                <div className={styles.bellHead} role="none">
                                    <span className={styles.bellTitle}>Powiadomienia</span>
                                    {unreadCount > 0 && (
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className={styles.bellReadAll}
                                            onClick={markAllRead}
                                        >
                                            <FiCheck size={13} aria-hidden="true" /> Oznacz wszystkie
                                        </button>
                                    )}
                                </div>
                                {notifications.length === 0 ? (
                                    // Pusty stan też jest pozycją menu: menu bez ani jednej
                                    // pozycji jest w ARIA niepoprawne, a komunikat i tak ma
                                    // zostać przeczytany zaraz po otwarciu panelu.
                                    <p
                                        className={styles.bellEmpty}
                                        role="menuitem"
                                        tabIndex={-1}
                                        aria-disabled="true"
                                    >
                                        Brak powiadomień.
                                    </p>
                                ) : (
                                    <div className={styles.bellList} role="none">
                                        {notifications.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                role="menuitem"
                                                className={`${styles.bellItem} ${
                                                    item.isRead ? "" : styles.bellItemUnread
                                                }`}
                                                onClick={() => openNotification(item)}
                                            >
                                                <span className={styles.bellItemTitle}>
                                                    {/* Stan „nieprzeczytane" niesie kropka i tło, więc dla
                                                        czytnika ekranu musi być powiedziany słowem. */}
                                                    {!item.isRead && (
                                                        <>
                                                            <span className={styles.bellItemDot} aria-hidden="true" />
                                                            <span className={styles.srOnly}>Nieprzeczytane: </span>
                                                        </>
                                                    )}
                                                    {item.title}
                                                </span>
                                                {item.message && (
                                                    <span className={styles.bellItemMsg}>{item.message}</span>
                                                )}
                                                <span className={styles.bellItemTime}>
                                                    {new Date(item.createdAt).toLocaleString("pl-PL")}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Dostępność (WCAG) — panel jak w mPracowniku, plus zmniejszony kontrast. */}
                    <AccessibilityMenu />

                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={toggleTheme}
                        title={theme === "light" ? "Tryb ciemny" : "Tryb jasny"}
                        aria-label={theme === "light" ? "Włącz tryb ciemny" : "Włącz tryb jasny"}
                    >
                        <span className={`${styles.themeIcon} ${theme === "dark" ? styles.themeRotate : ""}`}>
                            {theme === "light" ? <FiMoon size={16} /> : <FiSun size={16} />}
                        </span>
                    </button>

                    {mozeSkanowac && (
                        <button
                            type="button"
                            className={styles.actionBtn}
                            onClick={() => navigate(localizedPath("guard"))}
                            title="Weryfikacja QR"
                            aria-label="Weryfikacja QR"
                        >
                            <FiCheckSquare size={16} />
                        </button>
                    )}
                </div>

                <div className={styles.divider} />

                <div className={styles.userProfile} ref={menuRef}>
                    <button
                        type="button"
                        ref={userBtnRef}
                        className={styles.userTrigger}
                        onClick={() => setMenuOpen((open) => !open)}
                        title="Menu konta"
                        aria-label={`${fullName || user?.email || "Konto"} — menu konta`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        aria-controls={menuOpen ? USER_MENU_ID : undefined}
                    >
                        <span className={styles.headerAvatar} aria-hidden="true">{initials}</span>
                        <span className={styles.userInfo}>
                            <span className={styles.userName}>{fullName || user?.email || "…"}</span>
                            <span className={styles.userRole}>{USER_TYPE_LABELS[user?.type] || "Obsługa"}</span>
                        </span>
                        <FiChevronDown
                            size={14}
                            aria-hidden="true"
                            className={`${styles.miniChevron} ${menuOpen ? styles.miniChevronOpen : ""}`}
                        />
                    </button>

                    {menuOpen && (
                        <div
                            id={USER_MENU_ID}
                            ref={userMenuRef}
                            className={styles.dropdown}
                            role="menu"
                            aria-label="Menu konta"
                            tabIndex={-1}
                            onKeyDown={handleMenuKeyDown}
                        >
                            <div className={styles.userDropdownHeader} role="none">
                                <div className={styles.userDropdownAvatar} aria-hidden="true">{initials}</div>
                                <div className={styles.userDropdownMeta}>
                                    <span className={styles.userDropdownName}>{fullName}</span>
                                    <span className={styles.userDropdownRole}>
                                        {user?.departmentName || USER_TYPE_LABELS[user?.type] || "Obsługa"}
                                    </span>
                                    <span className={styles.userDropdownEmail}>{user?.email}</span>
                                </div>
                            </div>

                            <div className={styles.dropdownDivider} role="separator" />

                            <button
                                type="button"
                                role="menuitem"
                                className={styles.dropdownItem}
                                onClick={() => { setMenuOpen(false); navigate(localizedPath("dashboard")); }}
                            >
                                <FiGrid size={15} aria-hidden="true" />
                                <span className={styles.dropdownLabel}>Pulpit</span>
                            </button>

                            {adminTarget && (
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.dropdownItem}
                                    onClick={() => { setMenuOpen(false); navigate(localizedPath(adminTarget)); }}
                                >
                                    <FiSettings size={15} aria-hidden="true" />
                                    <span className={styles.dropdownLabel}>Panel administratora</span>
                                </button>
                            )}

                            <div className={styles.dropdownDivider} role="separator" />

                            <button
                                type="button"
                                role="menuitem"
                                className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                onClick={handleLogout}
                            >
                                <FiLogOut size={15} aria-hidden="true" />
                                <span className={styles.dropdownLabel}>Wyloguj się</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
